import React from "react";
import {
  CyclePhase,
  CyclePhaseInfo,
  CyclePhaseConfidence,
  CyclePhaseResult,
} from "../../types/cycle";

// Phase definitions
const PHASE_INFO: Record<string, CyclePhaseInfo> = {
  menstruation: {
    name: "Menstruation",
    description:
      "Low hormones increase inflammation and may heighten perceived effort. Training tolerance varies widely, so tune into your symptoms.",
    color: "text-red-700",
    bgColor: "bg-red-500/15",
  },
  early_follicular: {
    name: "Early Follicular",
    description:
      "Rising oestrogen and settling inflammation often bring improved energy and stable recovery. This phase supports building volume and refining skills.",
    color: "text-orange-700",
    bgColor: "bg-orange-500/15",
  },
  late_follicular: {
    name: "Late Follicular",
    description:
      "Peak oestrogen enhances neuromuscular function and carbohydrate use. Strength, power, and high-intensity work often feel more accessible.",
    color: "text-yellow-700",
    bgColor: "bg-yellow-500/15",
  },
  ovulatory: {
    name: "Ovulatory",
    description:
      "High oestrogen supports performance capacity, though connective tissue changes may affect joint stability. Movement quality awareness helps manage load.",
    color: "text-green-700",
    bgColor: "bg-green-500/15",
  },
  early_luteal: {
    name: "Early Luteal",
    description:
      "Rising progesterone elevates body temperature and shifts to fat metabolism. Cardiovascular strain may feel higher, making hydration and recovery key.",
    color: "text-pink-700",
    bgColor: "bg-pink-500/15",
  },
  late_luteal: {
    name: "Late Luteal",
    description:
      " Oestrogen and progesterone levels are decreasing, while inflammatory markers, fluid retention, and nervous system sensitivity may increase. Fatigue and reduced stress tolerance are common, so be open to training flexibility around how you are feeling.",
    color: "text-purple-700",
    bgColor: "bg-purple-500/15",
  },
};
/**
 * Determine cycle phase using scaled phase mapping based on cycle progress percentage
 * Implements SPEC 1 - Scaled Phase Mapping (Preferred)
 */
const determineCyclePhase = (
  cycle_day: number,
  average_cycle_length: number,
): CyclePhase => {
  // Use actual cycle length if available, otherwise default to 28 days
  const adjusted_cycle_length =
    average_cycle_length > 0 ? average_cycle_length : 28;

  // Calculate cycle progress as percentage (0-100)
  const cycle_progress = (cycle_day / adjusted_cycle_length) * 100;

  // Scaled Phase Mapping based on percentage of cycle
  if (cycle_progress <= 15) {
    return "menstruation"; // 0-15%
  } else if (cycle_progress <= 30) {
    return "early_follicular"; // 15-30%
  } else if (cycle_progress <= 45) {
    return "late_follicular"; // 30-45%
  } else if (cycle_progress <= 55) {
    return "ovulatory"; // 45-55%
  } else if (cycle_progress <= 75) {
    return "early_luteal"; // 55-75%
  } else {
    return "late_luteal"; // 75-100%
  }
};

/**
 * Determine phase confidence based on cycle data
 * Implements SPEC 1 - Phase Confidence
 */
const determinePhaseConfidence = (
  most_recent_cycle_start_date: string | undefined,
  cycle_day: number,
  average_cycle_length: number,
): CyclePhaseConfidence => {
  if (!most_recent_cycle_start_date) {
    return "low"; // No cycle start date logged
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const lastStart = new Date(most_recent_cycle_start_date);
  lastStart.setHours(0, 0, 0, 0);

  const daysSinceLastLog = Math.floor(
    (today.getTime() - lastStart.getTime()) / (1000 * 60 * 60 * 24),
  );

  // Low confidence: last log >45 days ago
  if (daysSinceLastLog > 45) {
    return "low";
  }

  // Medium confidence: cycle_day exceeds average_cycle_length
  if (average_cycle_length > 0 && cycle_day > average_cycle_length) {
    return "medium";
  }

  // High confidence: recent cycle_start_date logged and cycle_day within expected range
  return "high";
};

/**
 * Calculate current cycle progress and determine phase
 * Implements SPEC 1 - Core Principle and Derived Values
 */
const getCurrentCycleProgress = (
  most_recent_cycle_start_date: string,
  average_cycle_length: number,
): CyclePhaseResult | null => {
  if (!most_recent_cycle_start_date) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const lastStart = new Date(most_recent_cycle_start_date);
  lastStart.setHours(0, 0, 0, 0);

  const daysSinceStart = Math.floor(
    (today.getTime() - lastStart.getTime()) / (1000 * 60 * 60 * 24),
  );

  // cycle_day = (today_date − most_recent_cycle_start_date) + 1
  const cycle_day = daysSinceStart + 1; // Day 1 is the first day

  // If cycle_day < 1, no phase is assigned
  if (cycle_day < 1) return null;

  const adjusted_cycle_length =
    average_cycle_length > 0 ? average_cycle_length : 28;
  const cycle_progress = Math.min(
    (cycle_day / adjusted_cycle_length) * 100,
    100,
  );

  const cycle_phase = determineCyclePhase(cycle_day, average_cycle_length);
  const cycle_phase_confidence = determinePhaseConfidence(
    most_recent_cycle_start_date,
    cycle_day,
    average_cycle_length,
  );

  const phase_info = cycle_phase
    ? PHASE_INFO[cycle_phase]
    : PHASE_INFO.menstruation;

  return {
    cycle_phase,
    cycle_phase_confidence,
    cycle_day,
    cycle_progress,
    phase_info,
  };
};

interface CurrentCycleCardProps {
  most_recent_cycle_start_date: string;
  average_cycle_length: number;
  className?: string;
}

const CurrentCycleCard: React.FC<CurrentCycleCardProps> = ({
  most_recent_cycle_start_date,
  average_cycle_length,
  className = "",
}) => {
  const result = getCurrentCycleProgress(
    most_recent_cycle_start_date,
    average_cycle_length,
  );

  if (!result) return null;

  const { cycle_day, cycle_progress, phase_info } = result;
  const adjusted_cycle_length =
    average_cycle_length > 0 ? average_cycle_length : 28;

  return (
    <div
      className={`bg-base-100 rounded-none md:rounded-lg p-8 py-6 shadow-sm ${className}`}
    >
      <div className="flex justify-between">
        <h3 className="text-sm font-heading">
          Cycle{" "}
          <span className="font-grotesk text-base-content/60">Tracking</span>
        </h3>
        <p className=" text-base-content/80 text-md">
          Day{" "}
          <strong className="text-base bg-pink-500/15 rounded-full px-2 py-0.5">
            {cycle_day}
          </strong>{" "}
          {/* of {adjusted_cycle_length} */}
        </p>
      </div>

      {/* Current Phase */}
      <div className="mt-2">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-grotesk font-semibold text-base-content">
              {phase_info.name}{" "}
              <span className="text-base-content/60 font-light">phase</span>
            </h3>
            <p className="text-sm text-base-content/80 ">
              {phase_info.description}
            </p>
          </div>
        </div>
      </div>
      {/* Day Counter */}
      <div className="mb-4 mt-4">
        {/* Progress Bar */}
        <div className="w-full bg-base-200 rounded-full h-3 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-pink-500 to-pink-400 rounded-full transition-all duration-500"
            style={{ width: `${cycle_progress}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default CurrentCycleCard;
